<script lang="ts" setup>
import { computed } from "vue";
import AlertMessage from "@/components/messages/AlertMessage.vue";
import DeletedChip from "@/components/chips/DeletedChip.vue";
import { OrderStopType } from "@/constants/order";
import type { TripRouteFreight, TripRouteStop } from "@/types/trip";

const updatedStopFreights = defineModel<TripRouteFreight[]>({ required: true });

const props = defineProps<{
  routeFreight: TripRouteFreight;
  stop: TripRouteStop;
  routeStops: TripRouteStop[];
}>();

const emit = defineEmits<{
  (e: "open-stop-dialog", routeStop: TripRouteStop): void;
}>();

const isSelected = computed(() => {
  return updatedStopFreights.value.some((rf) => {
    return rf.id === props.routeFreight.id;
  });
});

const isAlreadyChosenOnAnotherDelivery = computed(() => {
  return props.routeStops.some((routeStop) => {
    return (
      routeStop.type === OrderStopType.delivery &&
      routeStop.id !== props.stop.id &&
      routeStop.freights.includes(props.routeFreight.id) &&
      !routeStop.isDeleted
    );
  });
});

const isDisabled = computed(() => {
  return !!props.stop.checkInTime || isAlreadyChosenOnAnotherDelivery.value || props.routeFreight.isDeleted;
});

const showSplitAlert = computed(() => {
  return isAlreadyChosenOnAnotherDelivery.value && !props.stop.freights.length;
});

const showSelectedAlert = computed(() => {
  return !isSelected.value && !isDisabled.value;
});

function selectFreight(value: boolean | null) {
  if (value) {
    updatedStopFreights.value.push(props.routeFreight);
  } else {
    updatedStopFreights.value = updatedStopFreights.value.filter((rf) => {
      return rf.id !== props.routeFreight.id;
    });
  }
}

const relatedPickUp = computed(() => {
  return props.routeStops.find((stop) => {
    return stop.type === OrderStopType.pickup && stop.freights.includes(props.routeFreight.id);
  });
});

const isRelatedPickUpCheckedIn = computed(() => {
  return Boolean(relatedPickUp.value?.checkInTime);
});

function openPickUpDetails() {
  if (relatedPickUp.value) {
    emit("open-stop-dialog", relatedPickUp.value);
  }
}
</script>

<template>
  <div>
    <v-tooltip :disabled="!isDisabled" location="top left">
      <template #activator="{ props: tooltipProps }">
        <div v-bind="tooltipProps">
          <div class="d-flex align-center">
            <v-checkbox
              :disabled="isDisabled"
              :label="'Route freight ' + routeFreight.name"
              :model-value="isSelected"
              color="primary"
              data-qa="select-route-freight"
              density="compact"
              hide-details
              @update:model-value="selectFreight"
            ></v-checkbox>

            <deleted-chip v-if="routeFreight.isDeleted" class="ml-3" />
          </div>
        </div>
      </template>

      This freight can’t be selected on this stop
    </v-tooltip>

    <alert-message v-if="showSelectedAlert" class="my-2">
      Select Route freight#{{ routeFreight.number }} at least at one DEL
    </alert-message>

    <alert-message v-if="showSplitAlert" class="my-2" content-class="d-flex align-center w-100 justify-space-between">
      <span>Split route freight at Pick-up #1 so it will be available to select on this delivery stop.</span>

      <v-btn v-if="isRelatedPickUpCheckedIn" color="primary" flat @click="openPickUpDetails">
        <span>Open pick up details</span>
      </v-btn>
    </alert-message>
  </div>
</template>
